<template>
  <AuthShell :title="t('auth.register')" :lead="t('auth.registerLead')" :background="background">
    <form class="auth-form" @submit.prevent="handleSubmit">
      <StateMessage v-if="formError" :title="t('auth.errorTitle')" :message="formError" tone="error" />

      <FormField
        v-model="email"
        type="email"
        :label="t('auth.email')"
        :placeholder="t('auth.enterEmail')"
        :error="fieldErrors.email"
        autocomplete="email"
        required
      />

      <FormField
        v-model="password"
        type="password"
        :label="t('auth.password')"
        :placeholder="t('auth.enterPassword')"
        :error="fieldErrors.password"
        :show-password-label="t('auth.showPassword')"
        :hide-password-label="t('auth.hidePassword')"
        autocomplete="new-password"
        required
      />

      <LandingButton type="submit" variant="accent" block :loading="isSubmitting">
        {{ isSubmitting ? t('auth.registering') : t('auth.register') }}
      </LandingButton>

      <p class="auth-form__separator"><span>{{ t('auth.orContinueWith') }}</span></p>

      <GoogleAuth mode="register" @success="onGoogleSuccess" @error="onGoogleError" />
    </form>

    <template #footer>
      <span class="auth__q">{{ t('auth.haveAccountQ') }}</span>
      <router-link :to="{ name: 'Login' }">{{ t('auth.haveAccountCta') }}</router-link>
    </template>
  </AuthShell>
</template>

<script setup>
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import AuthShell from '@/components/public/AuthShell.vue';
import FormField from '@/components/public/FormField.vue';
import StateMessage from '@/components/public/StateMessage.vue';
import LandingButton from '@/components/btn/LandingButton.vue';
import GoogleAuth from '@/components/Auth/GoogleAuth.vue';
import { ClientGameApi } from '@/api/backendService.js';
import background from '@/assets/images/backgrounds/auth-nave.webp';

const { t } = useI18n();
const router = useRouter();

const email = ref('');
const password = ref('');
const formError = ref('');
const fieldErrors = reactive({ email: '', password: '' });
const isSubmitting = ref(false);

/*
 * DRF answers with {field: [messages]} plus non_field_errors. The page used to
 * print the raw keys, so users read "non_field_errors" and comma-joined arrays.
 */
/*
 * first_name/last_name are deliberately absent.
 *
 * Mapping a key here that no field renders would write the message into
 * fieldErrors, satisfy the hasFieldError check below, and then display
 * nothing — the user would see a rejected form with no reason given. Left
 * unmapped, anything the server says about a name surfaces in formError.
 */
const FIELD_MAP = {
  email: 'email',
  password: 'password',
};

const asText = (value) => (Array.isArray(value) ? value.join(' ') : String(value));

const clearErrors = () => {
  formError.value = '';
  Object.keys(fieldErrors).forEach((key) => {
    fieldErrors[key] = '';
  });
};

const applyErrors = (data) => {
  const unmapped = [];

  Object.entries(data || {}).forEach(([key, value]) => {
    const target = FIELD_MAP[key];
    if (target) {
      fieldErrors[target] = asText(value);
    } else {
      unmapped.push(asText(value));
    }
  });

  const hasFieldError = Object.values(fieldErrors).some(Boolean);
  if (unmapped.length) {
    formError.value = unmapped.join(' ');
  } else if (!hasFieldError) {
    formError.value = t('auth.unexpectedError');
  }
};

const handleSubmit = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  clearErrors();

  try {
    const response = await ClientGameApi.clientRegisterLocalClientCreate({
      email: email.value,
      password: password.value,
    });

    if (response.status === 201) {
      /*
       * The account exists but cannot sign in yet, so sending people to the
       * login form would only earn them a rejection. They go to the activation
       * page instead, which explains the email and takes the code by hand.
       */
      router.push({
        name: 'Activate',
        query: { pending: '1', email: email.value },
      });
    }
  } catch (error) {
    if (error.response && error.response.status === 400) {
      applyErrors(error.response.data);
    } else {
      formError.value = t('auth.unexpectedError');
    }
  } finally {
    isSubmitting.value = false;
  }
};

const onGoogleSuccess = () => {
  /* GoogleAuth dispatches to the store and redirects itself. */
};

const onGoogleError = ({ error }) => {
  formError.value = error;
};
</script>

<style scoped>
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.auth-form__separator {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin: 0;
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.auth-form__separator::before,
.auth-form__separator::after {
  content: '';
  flex: 1;
  border-top: 1px solid var(--border);
}
</style>
